import { useRef } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router';
import { useApiService } from '../contexts/ApiServiceContext';
import { useAppInitialization } from '../hooks/useAppInitialization';
import { useGlobalErrorHandlers } from '../hooks/useGlobalErrorHandlers';
import { routeFactory } from '../routeFactory';
import { AppInitializer } from './startup/AppInitializer';
import { TestDatabaseIndicator } from './startup/TestDatabaseIndicator';
import { Logger } from '../utils/Logger';

/**
 * Main app content component
 *
 * Responsibilities:
 * - Initialize global error handlers
 * - Orchestrate Service Worker initialization and version checking
 * - Create router once app is ready
 * - Render app content with loading screen and test database indicator
 */
export function AppContent() {
  const apiService = useApiService();

  // Configurer les gestionnaires d'erreurs globaux et l'orchestration du Service Worker
  useGlobalErrorHandlers();
  const { isLoading } = useAppInitialization();

  // Créer le routeur une seule fois, uniquement après que l'API est initialisée.
  // Évite que createBrowserRouter démarre les loaders avant getUserDetail() → redirect /login inutile.
  const routerRef = useRef<ReturnType<typeof createBrowserRouter> | null>(null);

  // Afficher l'écran d'initialisation pendant le chargement
  if (isLoading) {
    Logger.debug('APP', 'Chargement de l\'app...');
    return <AppInitializer />;
  }

  if (!routerRef.current) {
    Logger.debug('APP', 'Création du routeur');
    routerRef.current = createBrowserRouter(routeFactory(apiService));
    Logger.debug('APP', 'Routeur créé:', !!routerRef.current);
  }

  Logger.debug('APP', 'Rendu avec le routeur');
  return (
    <>
      <TestDatabaseIndicator />
      <RouterProvider router={routerRef.current} />
    </>
  );
}
